import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import https from "https";
import axios from "axios";
import log from "../utils/log";
import metaData from "../data/metaData";
import c from "../utils/common";

//
import useAuthStore from "../states/useAuth";
import useRequestStore from "../states/useRequest";
import useProductStore from "../states/useProduct";
import useDB from "./useDB";

const useProduct = () => {
  const { auth, user } = useAuthStore();
  const {
    getSingleProduct: getSingleProductFromCache,
    getProducts: getProductsFromCache,
    updateSingleProductCache,
    updateProductsCache,
  } = useProductStore();

  const { get } = useDB();

  /**
   * getSingleProduct:
   * This function fetches a single product by its ID, first checking if the product's data is cached.
   *
   * - If the product data is found in the cache, it returns the cached data.
   * - If the product data is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary object), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {number|string} id - The unique ID of the product to fetch.
   * @returns {Object} - The product data, either from the cache or fetched via API.
   */
  const getSingleProduct = async (id, saveCache = true, useCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = useCache ? getSingleProductFromCache(id) : null;

    if (!cache) {
      log.debug("no cache, axios");

      let response = await get("product/query", {
        id,
        populate: [
          {
            path: "brand",
            select: "name logo category",
          },
          {
            path: "redeemShops",
            select: "name logo address companyId brandId",
            populate: [
              {
                path: "company",
                select: "name",
              },
              {
                path: "brand",
                select: "name logo",
              },
            ],
          },
        ],
      });

      // Cache the fetched product data
      if (saveCache) {
        updateSingleProductCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  /**
   * getProducts:
   * This function fetches a list of products based on a query (such as pagination or filters),
   * first checking if the query result is cached.
   *
   * - If the query result is found in the cache, it returns the cached data.
   * - If the query result is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary array), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {string|Object|undefined} query - The query used to filter or paginate the products.
   * @returns {Array} - The list of products, either from the cache or fetched via API.
   */
  const getProducts = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getProductsFromCache(query) : null;
    if (cache) {
      log.debug("using cache");
      return cache; // Return cached data if available
    }

    log.debug("no cache, axios");

    // prepare body
    let body;
    if (query === undefined || typeof query === "string") {
      //transform to json
      body = {
        companyId: user?.companyId,
        populate: {
          path: "brand",
          select: "name logo",
        },
      };
    } else if (typeof query === "object") {
      // If query is an object, assume it's already in the correct format
      body = query;
    } else {
      throw new Error("Invalid query format. It must be either undefined, a string, or an object.");
    }

    let response = await get("products/query", body);

    // Cache the fetched requests data
    if (typeof query !== "object" && saveCache) {
      updateProductsCache("update", query, response); // Cache the fetched products data
    }
    return response; // Return the fetched data
  };

  const updateProduct = async (productId, data, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "product", data, {
      params: { id: productId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleProductCache("update", [response.data]);
    }
    return response.data; // Return the updated data
  };

  return { getSingleProduct, getProducts, updateProduct };
};

export default useProduct;
